import { BLUFF_CATS, TRUF_CATS, type SpyData } from "@shared/content";
import type { BluffSettings } from "@shared/games/bluff";
import type { MatchSettings } from "@shared/games/match";
import type { SpySettings } from "@shared/games/spy";
import type { TrufSettings } from "@shared/games/truf";
import type { Lang, RoomView } from "@shared/types";
import { useEffect, useState } from "react";
import { Btn } from "../../components/Button";
import { ChipSelect, Section, Segmented, Toggle } from "../../components/Controls";
import { Sheet } from "../../components/Sheet";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { IS_DEMO } from "../../lib/asset";
import { localContent } from "../../lib/local-content";
import { GuessSettingsPanel } from "../guess/RoomSettings";

interface P<S> {
  settings: S;
  editable: boolean;
  set(patch: Partial<S>): void;
}

/** 18+ switch that asks everyone to confirm their age first. */
function AdultToggle({ value, editable, onChange }: { value: boolean; editable: boolean; onChange(v: boolean): void }) {
  const { d } = useI18n();
  const [ask, setAsk] = useState(false);
  return (
    <>
      <Toggle
        checked={value}
        disabled={!editable}
        label={d.lobby.adult}
        onChange={(v) => {
          if (v) setAsk(true);
          else onChange(false);
        }}
      />
      <Sheet open={ask} onClose={() => setAsk(false)} title={d.lobby.adultConfirmTitle} size="narrow">
        <p className="text-lg text-ink-2">{d.lobby.adultConfirmText}</p>
        <div className="mt-6 flex flex-col gap-3">
          <Btn
            block
            onClick={() => {
              onChange(true);
              setAsk(false);
            }}
          >
            {d.lobby.adultConfirmYes}
          </Btn>
          <Btn block variant="soft" onClick={() => setAsk(false)}>
            {d.common.cancel}
          </Btn>
        </div>
      </Sheet>
    </>
  );
}

export function BluffSettingsPanel({ settings: s, editable, set }: P<BluffSettings>) {
  const { d } = useI18n();
  const cats = BLUFF_CATS.filter((c) => c !== "night");
  return (
    <div className="space-y-6">
      <Section title={d.bluffSettings.length}>
        <Segmented
          disabled={!editable}
          value={s.length}
          onChange={(v) => set({ length: v })}
          options={[
            { value: "short", label: d.bluffSettings.short },
            { value: "normal", label: d.bluffSettings.normal },
            { value: "long", label: d.bluffSettings.long },
          ]}
        />
      </Section>
      <Section title={d.bluffSettings.speed}>
        <Segmented
          disabled={!editable}
          value={s.speed}
          onChange={(v) => set({ speed: v })}
          options={[
            { value: "chill", label: d.bluffSettings.chill },
            { value: "normal", label: d.bluffSettings.normalSpeed },
            { value: "fast", label: d.bluffSettings.fast },
          ]}
        />
      </Section>
      <Section title={d.bluffSettings.cats}>
        <ChipSelect
          disabled={!editable}
          values={s.cats.filter((c) => c !== "night")}
          allLabel={d.bluffSettings.allCats}
          onChange={(v) => set({ cats: v })}
          options={cats.map((c) => ({ value: c, label: (d.cats as Record<string, string>)[c] }))}
        />
      </Section>
      <Section title={d.bluffSettings.help}>
        <Segmented disabled={!editable} value={s.help} onChange={(v) => set({ help: v })} options={[0, 1, 2, 3].map((n) => ({ value: n, label: String(n) }))} />
      </Section>
      <div className="surface-2 px-4 py-2">
        <Toggle checked={s.houseLie} disabled={!editable} label={d.bluffSettings.houseLie} onChange={(v) => set({ houseLie: v })} />
        <Toggle checked={s.jokes !== false} disabled={!editable} label={d.bluffSettings.jokes} onChange={(v) => set({ jokes: v })} />
        <AdultToggle value={s.adult} editable={editable} onChange={(v) => set({ adult: v })} />
      </div>
    </div>
  );
}

function useSpyData(lang: Lang, local: boolean): SpyData | null {
  const [data, setData] = useState<SpyData | null>(() => (local || IS_DEMO ? localContent.spy(lang) : null));
  useEffect(() => {
    if (local || IS_DEMO) return;
    let alive = true;
    api<SpyData>(`/content/spy/${lang}`)
      .then((r) => alive && setData(r))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [lang, local]);
  return data;
}

export function SpySettingsPanel({ settings: s, editable, set, lang, local }: P<SpySettings> & { lang: Lang; local: boolean }) {
  const { d } = useI18n();
  const data = useSpyData(lang, local);
  const packs = (data?.packs ?? []).filter((p) => s.adult || !p.adult);
  return (
    <div className="space-y-6">
      <Section title={d.spy.packs}>
        <ChipSelect
          disabled={!editable}
          values={s.packs}
          allLabel={d.spy.allPacks}
          onChange={(v) => set({ packs: v })}
          options={packs.map((p) => ({ value: p.id, label: `${p.emoji} ${p.name}` }))}
        />
      </Section>
      <Section title={d.spy.spies}>
        <Segmented
          disabled={!editable}
          value={s.spies}
          onChange={(v) => set({ spies: v })}
          options={[{ value: 0, label: d.spy.spiesAuto }, ...[1, 2, 3, 4].map((n) => ({ value: n, label: String(n) }))]}
        />
      </Section>
      <Section title={d.spy.questions}>
        <Segmented disabled={!editable} value={s.questions} onChange={(v) => set({ questions: v })} options={[3, 4, 5].map((n) => ({ value: n, label: String(n) }))} />
      </Section>
      <Section title={d.spy.mode}>
        <Segmented
          disabled={!editable || local}
          value={local ? "voice" : s.mode}
          onChange={(v) => set({ mode: v })}
          options={[
            { value: "voice", label: d.spy.modeVoice },
            { value: "text", label: d.spy.modeText },
          ]}
        />
      </Section>
      <Section title={d.spy.qTime}>
        <Segmented
          disabled={!editable}
          value={s.qTime}
          onChange={(v) => set({ qTime: v })}
          options={[{ value: 0, label: d.spy.qTimeNone }, ...[45, 60, 90].map((n) => ({ value: n, label: d.common.seconds.replace("{n}", String(n)) }))]}
        />
      </Section>
      <div className="surface-2 px-4 py-2">
        <Toggle checked={s.spyHint} disabled={!editable} label={d.spy.spyHintSetting} onChange={(v) => set({ spyHint: v })} />
        <AdultToggle value={s.adult} editable={editable} onChange={(v) => set({ adult: v })} />
      </div>
    </div>
  );
}

export function MatchSettingsPanel({ settings: s, editable, set }: P<MatchSettings>) {
  const { d } = useI18n();
  const themes = ["supra", "animals", "party", "tbilisi", "emoji"] as const;
  return (
    <div className="space-y-6">
      <Section title={d.match.theme}>
        <Segmented disabled={!editable} value={s.theme} onChange={(v) => set({ theme: v })} options={themes.map((t) => ({ value: t, label: d.match.themes[t] }))} />
      </Section>
      <Section title={d.match.size}>
        <Segmented disabled={!editable} value={s.pairs} onChange={(v) => set({ pairs: v })} options={[8, 12, 18, 24].map((n) => ({ value: n, label: `${n * 2}` }))} />
      </Section>
      <Section title={d.match.bombs}>
        <Segmented disabled={!editable} value={s.bombs} onChange={(v) => set({ bombs: v })} options={[{ value: 0, label: d.match.bombsNone }, { value: 2, label: "2" }, { value: 4, label: "4" }]} />
      </Section>
      <Section title={d.match.turnTime}>
        <Segmented
          disabled={!editable}
          value={s.turnTime}
          onChange={(v) => set({ turnTime: v })}
          options={[{ value: 0, label: d.match.turnTimeNone }, ...[10, 15, 20].map((n) => ({ value: n, label: d.common.seconds.replace("{n}", String(n)) }))]}
        />
      </Section>
    </div>
  );
}

export function TrufSettingsPanel({ settings: s, editable, set }: P<TrufSettings>) {
  const { d } = useI18n();
  const cats = TRUF_CATS.filter((c) => c !== "night");
  return (
    <div className="space-y-6">
      <Section title={d.truf.count}>
        <Segmented disabled={!editable} value={s.count} onChange={(v) => set({ count: v })} options={[10, 15, 20, 30].map((n) => ({ value: n, label: String(n) }))} />
      </Section>
      <Section title={d.truf.time}>
        <Segmented disabled={!editable} value={s.time} onChange={(v) => set({ time: v })} options={[7, 10, 15].map((n) => ({ value: n, label: d.common.seconds.replace("{n}", String(n)) }))} />
      </Section>
      <Section title={d.truf.cats}>
        <ChipSelect
          disabled={!editable}
          values={s.cats.filter((c) => c !== "night")}
          allLabel={d.truf.allCats}
          onChange={(v) => set({ cats: v })}
          options={cats.map((c) => ({ value: c, label: (d.cats as Record<string, string>)[c] }))}
        />
      </Section>
      <div className="surface-2 px-4 py-2">
        <AdultToggle value={s.adult} editable={editable} onChange={(v) => set({ adult: v })} />
      </div>
    </div>
  );
}

export function SettingsFor({ view, editable, set }: { view: RoomView; editable: boolean; set(patch: Record<string, unknown>): void }) {
  switch (view.game) {
    case "bluff":
      return <BluffSettingsPanel settings={view.settings} editable={editable} set={set} />;
    case "spy":
      return <SpySettingsPanel settings={view.settings} editable={editable} set={set} lang={view.lang} local={view.local} />;
    case "match":
      return <MatchSettingsPanel settings={view.settings} editable={editable} set={set} />;
    case "truf":
      return <TrufSettingsPanel settings={view.settings} editable={editable} set={set} />;
    case "guess":
      return <GuessSettingsPanel settings={view.settings} editable={editable} set={set} lang={view.lang} />;
  }
}
